// Create new file: src/components/auth/ResetPasswordForm.jsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  LinearProgress
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  CheckCircle, 
  Lock,
  Person 
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';

const ResetPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid reset link. Please request a new password reset.');
      setLoading(false);
      return;
    }
    
    setToken(tokenParam);
    verifyToken(tokenParam);
  }, [searchParams]);

  const verifyToken = async (tokenString) => {
    try {
      const response = await api.post('/auth/verify-reset-token', { 
        token: tokenString 
      });
      
      if (response.data.valid) {
        setTokenValid(true);
        setUserInfo(response.data.user);
      } else {
        setError(response.data.error || 'Invalid or expired reset token');
      }
    } catch (error) {
      console.error('Token verification error:', error);
      const errorMessage = error.response?.data?.error || 'Invalid or expired reset token';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('One number');
    }
    return errors;
  };

  const getPasswordStrength = (password) => {
    const errors = validatePassword(password);
    if (password.length === 0) return { strength: 0, label: '', color: 'inherit' };
    if (errors.length > 2) return { strength: 25, label: 'Weak', color: 'error' };
    if (errors.length > 0) return { strength: 50, label: 'Fair', color: 'warning' };
    if (password.length < 12) return { strength: 75, label: 'Good', color: 'info' };
    return { strength: 100, label: 'Strong', color: 'success' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setError(`Password must have: ${passwordErrors.join(', ')}`);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await api.post('/auth/reset-password', { 
        token, 
        new_password: newPassword 
      });
      setSuccess(true);
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <Card sx={{ maxWidth: 450, mx: 'auto', borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Verifying reset link...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (success) {
    return (
      <Card sx={{ maxWidth: 450, mx: 'auto', borderRadius: 2, overflow: 'hidden' }}>
        <Box 
          sx={{ 
            height: 8, 
            width: '100%', 
            background: 'linear-gradient(90deg, #46a07a 0%, #2f7d5b 100%)' 
          }} 
        />
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          
          <Typography variant="h4" component="h1" gutterBottom fontWeight={700} color="success.main">
            Password Reset Complete!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your password has been successfully reset. You can now log in with your new password.
          </Typography>
          
          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            onClick={handleGoToLogin}
            sx={{ py: 1.5, borderRadius: 1.5 }}
          >
            Go to Login
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!tokenValid) {
    return (
      <Card sx={{ maxWidth: 450, mx: 'auto', borderRadius: 2, overflow: 'hidden' }}>
        <Box 
          sx={{ 
            height: 8, 
            width: '100%', 
            background: 'linear-gradient(90deg, #cf6f61 0%, #a84a3f 100%)' 
          }} 
        />
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Lock sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          
          <Typography variant="h4" component="h1" gutterBottom fontWeight={700} color="error.main">
            Invalid Reset Link
          </Typography>
          
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This could happen if:
          </Typography>
          <ul style={{ textAlign: 'left', color: '#666', marginBottom: '24px' }}>
            <li>The link has expired (links expire after 15 minutes)</li>
            <li>The link has already been used</li>
            <li>The link was copied incorrectly</li>
          </ul>
          
          <Button
            variant="contained"
            onClick={() => navigate('/forgot-password')}
            sx={{ mr: 2 }}
          >
            Request New Reset Link
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleGoToLogin}
          >
            Back to Login
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 450, mx: 'auto', borderRadius: 2, overflow: 'hidden' }}>
      <Box 
        sx={{ 
          height: 8, 
          width: '100%', 
          background: 'linear-gradient(90deg, #5980a6 0%, #3f6288 100%)' 
        }} 
      />
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight={700} color="primary">
          Reset Password
        </Typography>
        
        {userInfo && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Person sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Resetting password for: <strong>{userInfo.first_name} {userInfo.last_name}</strong>
            </Typography>
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <TextField
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="normal"
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitting}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={submitting}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          
          {newPassword && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Password Strength
                </Typography>
                <Typography variant="caption" color={`${passwordStrength.color}.main`}>
                  {passwordStrength.label}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={passwordStrength.strength} 
                color={passwordStrength.color}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}
          
          <TextField
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            fullWidth
            margin="normal"
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={submitting}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={submitting}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={submitting}
            sx={{ 
              py: 1.5,
              mb: 3,
              borderRadius: 1.5,
              boxShadow: '0 4px 10px rgba(37, 99, 235, 0.25)',
              '&:hover': {
                boxShadow: '0 6px 15px rgba(37, 99, 235, 0.35)',
              }
            }}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Remember your password?{' '}
            <Button
              variant="text"
              onClick={handleGoToLogin}
              sx={{ 
                color: 'primary.main',
                textTransform: 'none',
                p: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                }
              }}
            >
              Back to Login
            </Button>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ResetPasswordForm;